import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { identityApi, setIdentityToken } from '@/api/identity.api';
import { STORAGE_KEYS } from '@/constants/storage';
import type { IdentityLoginResponse, IdentitySession, PortalCode } from '@/types/identity';
import { portalCodeToPath } from '@/config/portalConfig';

interface IdentityState {
  session: IdentitySession | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  initialized: boolean;
}

function responseToSession(data: IdentityLoginResponse): IdentitySession | null {
  if (!data.identityId || !data.activeProfileId || !data.activePortalCode || !data.organizationId) {
    return null;
  }
  return {
    identityId: data.identityId,
    identityUuid: data.identityUuid,
    email: data.email,
    phone: data.phone,
    activeProfileId: data.activeProfileId,
    activePortalCode: data.activePortalCode as PortalCode,
    organizationId: data.organizationId,
    organizationName: data.organizationName,
    permissions: data.permissions ?? [],
    availablePortals: data.availablePortals ?? [],
  };
}

function loadStoredIdentity(): Pick<IdentityState, 'session' | 'accessToken' | 'isAuthenticated'> {
  const accessToken = localStorage.getItem(STORAGE_KEYS.IDENTITY_TOKEN);
  const sessionRaw = localStorage.getItem(STORAGE_KEYS.IDENTITY_SESSION);
  if (!accessToken || !sessionRaw) {
    return { session: null, accessToken: null, isAuthenticated: false };
  }
  try {
    const session = JSON.parse(sessionRaw) as IdentitySession;
    return { session, accessToken, isAuthenticated: true };
  } catch {
    return { session: null, accessToken: null, isAuthenticated: false };
  }
}

function persistSession(data: IdentityLoginResponse) {
  if (data.accessToken) {
    setIdentityToken(data.accessToken);
  }
  const session = responseToSession(data);
  if (session) {
    localStorage.setItem(STORAGE_KEYS.IDENTITY_SESSION, JSON.stringify(session));
  }
}

export const identityLogin = createAsyncThunk(
  'identity/login',
  async ({ identifier, password, totpCode }: { identifier: string; password: string; totpCode?: string }) => {
    return identityApi.login(identifier, password, totpCode);
  },
);

export const identityVerifyMagicLink = createAsyncThunk(
  'identity/verifyMagicLink',
  async (token: string) => identityApi.verifyMagicLink(token),
);

export const identitySwitchPortal = createAsyncThunk(
  'identity/switchPortal',
  async ({ profileId, portalCode }: { profileId: number; portalCode: string }) =>
    identityApi.switchPortal(profileId, portalCode),
);

export const identityRestoreSession = createAsyncThunk('identity/restore', async () => identityApi.me());

const initialStored = loadStoredIdentity();

const identitySlice = createSlice({
  name: 'identity',
  initialState: {
    session: initialStored.session,
    accessToken: initialStored.accessToken,
    isAuthenticated: initialStored.isAuthenticated,
    loading: false,
    initialized: false,
  } as IdentityState,
  reducers: {
    setIdentityInitialized(state) {
      state.initialized = true;
    },
    identityLogout(state) {
      state.session = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      setIdentityToken(null);
      localStorage.removeItem(STORAGE_KEYS.IDENTITY_SESSION);
    },
    applyIdentityLogin(state, action: PayloadAction<IdentityLoginResponse>) {
      const session = responseToSession(action.payload);
      state.session = session;
      state.accessToken = action.payload.accessToken ?? null;
      state.isAuthenticated = Boolean(session && action.payload.accessToken);
      persistSession(action.payload);
    },
  },
  extraReducers: (builder) => {
    const handleLoginFulfilled = (state: IdentityState, action: PayloadAction<IdentityLoginResponse>) => {
      state.loading = false;
      if (action.payload.mfaRequired) return;
      const session = responseToSession(action.payload);
      state.session = session;
      state.accessToken = action.payload.accessToken ?? null;
      state.isAuthenticated = Boolean(session && action.payload.accessToken);
      persistSession(action.payload);
    };

    builder
      .addCase(identityLogin.pending, (state) => { state.loading = true; })
      .addCase(identityLogin.fulfilled, handleLoginFulfilled)
      .addCase(identityLogin.rejected, (state) => { state.loading = false; })
      .addCase(identityVerifyMagicLink.pending, (state) => { state.loading = true; })
      .addCase(identityVerifyMagicLink.fulfilled, handleLoginFulfilled)
      .addCase(identityVerifyMagicLink.rejected, (state) => { state.loading = false; })
      .addCase(identitySwitchPortal.fulfilled, handleLoginFulfilled)
      .addCase(identityRestoreSession.fulfilled, (state, action) => {
        const session = responseToSession(action.payload);
        if (session) {
          state.session = session;
          state.isAuthenticated = true;
          localStorage.setItem(STORAGE_KEYS.IDENTITY_SESSION, JSON.stringify(session));
        }
      });
  },
});

export const { setIdentityInitialized, identityLogout, applyIdentityLogin } = identitySlice.actions;

export function getActivePortalHomePath(session: IdentitySession | null): string {
  if (!session) return '/identity/login';
  return portalCodeToPath(session.activePortalCode);
}

export default identitySlice.reducer;
